import { atom } from 'jotai';
import { atomWithSafeLocalStorage } from '@/recoil/jotaiStorage';
import type { AppLanguageCode } from '@/utils/language';

/**
 * 회의록 문서 번역 상태.
 *
 * ## 왜 문서별 맵인가
 *
 * TalkFLowModal 은 전역에 상시 마운트되는 인스턴스가 여러 개다
 * (RecorderProvider 의 녹음 위젯 / NewNotificationsPopup / GNB 알람).
 * 그래서 `/talkflow/A` 페이지를 연 채 알림에서 B 를 여는 식으로
 * **TalkFLow 가 동시에 2개 이상 마운트될 수 있다.**
 *
 * 상태를 단일 슬롯으로 두면 B 를 번역하는 순간 A 의 결과가 밀려나 A 화면이 말없이
 * 원문으로 돌아간다. 외국어 계정은 문서를 열기만 해도 자동 번역이 돌기 때문에
 * 사용자가 아무것도 누르지 않아도 발생한다 — 이 기능이 없애려던 화면이 다시 나오는 셈이다.
 * 그래서 모든 상태를 docId 로 나눠 담는다.
 *
 * ## 왜 영속하지 않나
 *
 * 결과를 localStorage 에 남기면 용량 한도 때문에 보관 개수를 제한해야 하고,
 * 그 순간 "문서를 여러 개 열면 오래된 것부터 밀려남" 이 되돌아온다.
 * 영속의 이득은 "새로고침 후에도 번역 유지" 하나뿐인데,
 * 외국어 계정은 새로고침하면 자동 번역이 다시 돌아 체감이 거의 없다.
 * 그래서 결과는 **세션 메모리에만** 두고 개수 제한 없이 문서별로 보관한다.
 *
 * 도착 언어만 영속한다 — 문서와 무관한 사용자 취향이라 다음에 열 때 이어 쓰는 게 자연스럽다.
 */
export type RecordTranslationEntry = {
  targetLanguage: AppLanguageCode;
  /**
   * 번역 시점의 원문 지문(useRecordTranslationSource). 현재 지문과 다르면
   * "번역 후 원문이 바뀐" 상태이며, 띠가 경고로 전환된다.
   */
  sourceFingerprint: string;
  /** 번역본을 화면에 적용 중인지. 취소는 이 값만 false 로 → 결과는 캐시된 채 재호출 없이 복귀 */
  isApplied: boolean;
  subject: string;
  summary: string;
  content: string;
  memo: string;
};

/** docId → 번역 결과. 세션 메모리 전용(영속 X) */
export const recordTranslationEntriesAtom = atom<
  Record<string, RecordTranslationEntry>
>({});

/** 번역 요청이 진행 중인 문서 id 집합 (동시에 2개가 번역될 수 있어 맵으로 둔다) */
export const recordTranslatingDocIdsAtom = atom<Record<string, boolean>>({});

/**
 * "원문 보기" 로 전환한 문서 id 집합.
 * 취소(번역 표시 해제)와 달리 번역 상태를 유지한 채 표시만 원문으로 바꾸는 것이다.
 */
export const recordShowOriginalDocIdsAtom = atom<Record<string, boolean>>({});

// 마지막으로 선택한 도착 언어. 문서와 무관한 사용자 취향이라 유일하게 영속한다.
export const recordTranslationToLangAtom =
  atomWithSafeLocalStorage<AppLanguageCode | null>(
    'record_translation_to_lang',
    null,
  );
